import { useMaybeRoomContext } from '@livekit/components-react';
import { RoomEvent } from 'livekit-client';
import { useEffect, useState } from 'react';
import { Platform } from 'react-native';

const LEVEL_THRESHOLD = 0.001;

type UseMicAudioLevelOptions = {
  audioStream: MediaStream | null;
  enabled: boolean;
};

/**
 * Normalized mic level (0–1). Prefers LiveKit local participant audioLevel when
 * the mic is published; falls back to a Web Audio AnalyserNode on getUserMedia.
 */
export function useMicAudioLevel({ audioStream, enabled }: UseMicAudioLevelOptions): number {
  const room = useMaybeRoomContext();
  const [analyserLevel, setAnalyserLevel] = useState(0);
  const [liveKitLevel, setLiveKitLevel] = useState(0);

  useEffect(() => {
    if (!room || !enabled) {
      setLiveKitLevel(0);
      return undefined;
    }

    const syncLevel = () => {
      setLiveKitLevel(room.localParticipant.audioLevel ?? 0);
    };

    syncLevel();
    room.on(RoomEvent.ActiveSpeakersChanged, syncLevel);
    room.on(RoomEvent.LocalTrackPublished, syncLevel);
    room.on(RoomEvent.LocalTrackUnpublished, syncLevel);
    return () => {
      room.off(RoomEvent.ActiveSpeakersChanged, syncLevel);
      room.off(RoomEvent.LocalTrackPublished, syncLevel);
      room.off(RoomEvent.LocalTrackUnpublished, syncLevel);
    };
  }, [room, enabled]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !enabled || !audioStream) {
      setAnalyserLevel(0);
      return undefined;
    }

    const audioContext = new AudioContext();
    const source = audioContext.createMediaStreamSource(audioStream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);

    const buffer = new Uint8Array(analyser.frequencyBinCount);
    let frameId = 0;

    const tick = () => {
      analyser.getByteFrequencyData(buffer);
      let sum = 0;
      for (let i = 0; i < buffer.length; i += 1) {
        sum += buffer[i];
      }
      const avg = sum / buffer.length / 255;
      setAnalyserLevel(avg);
      frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frameId);
      source.disconnect();
      analyser.disconnect();
      audioContext.close().catch(() => undefined);
    };
  }, [audioStream, enabled]);

  const level = liveKitLevel >= LEVEL_THRESHOLD ? liveKitLevel : analyserLevel;
  return enabled ? level : 0;
}
